import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { container } from "@infrastructure/container";
import { Product } from "@domain/product/models/Product";
import { ProductFilter, ProductStatus } from "@domain/product/models/ProductFilter";
import { PaginatedResource } from "@domain/base/Resource";


export const useProducts = (filter?: ProductFilter, status?: ProductStatus) => {

    const queryClient = useQueryClient();

    const {
        data: productsResponse,
        isLoading: isLoadingProducts,
        isFetching: isFetchingProducts,
    } = useQuery<PaginatedResource<Product[]>>({
        queryKey: ['products', filter, status],
        queryFn: async () => {
            const result = await container.productRepository.getProducts(filter, status);
            if (result.status === 'error') throw new Error(result.error.message);
            return result;
        },
        enabled: true,
        refetchOnWindowFocus: false,
    });

    const { mutateAsync: bulkEditProducts } = useMutation({
        mutationFn: async ({ updateData, filters }: { updateData: Partial<Product>; filters?: ProductFilter }) => {
            const result = await container.productRepository.bulkUpdateProducts({ updateData, filters });

            if (result.status === 'error') {
                throw new Error(result.error.message);
            }
        },
        onSuccess: () => {
            // Invalidate products list to refetch (handles both selected and "all" updates)
            queryClient.invalidateQueries({ queryKey: ['products'] });
        },
        onError: (error) => {
            console.error('Failed to bulk update products:', error);
        }
    });

    const { mutateAsync: bulkDeleteProducts } = useMutation({
        mutationFn: async (filters: ProductFilter) => {
            const results = await container.productRepository.deleteProducts(filters);

            if (results.status === 'error') {
                throw new Error(results.error.message);
            }

            return filters;
        },
        onSuccess: (filters) => {
            filters.productIds?.forEach(productId => {
                queryClient.removeQueries({ queryKey: ['product', productId] });
            });
            queryClient.invalidateQueries({ queryKey: ['products'] });
        },
        onError: (error) => {
            console.error('Failed to bulk delete products:', error);
        }
    });

    return {
        products: productsResponse,
        isLoading: isLoadingProducts,
        isFetching: isFetchingProducts,
        bulkEditProducts,
        bulkDeleteProducts
    };
};

